/**
 * Resolves the WhatsApp deep link for a phone number.
 *
 * Rules (in order):
 * 1. A backend-provided URL always wins - the backend builds it from the
 *    stored `country_code` + `phone_number`, so it is already a full
 *    international number.
 * 2. A masked number (e.g. `+65****4567`) never gets a link - the backend
 *    sends `whatsapp_url = null` for those and guessing would open the wrong
 *    chat.
 * 3. Otherwise a `+`-prefixed number becomes `https://wa.me/<digits>`.
 * 4. A local number without a country code is never linked - we do not guess.
 *
 * @example
 * resolveWhatsAppUrl('+65 9123 4567'); // 'https://wa.me/6591234567'
 * resolveWhatsAppUrl('91234567'); // null - no country code
 * resolveWhatsAppUrl('+65****4567'); // null - masked number
 */
import { isMaskedValue } from '@/shared/utils/isMaskedValue';

export function resolveWhatsAppUrl(number: string, backendUrl?: string | null): string | null {
  const backend = backendUrl?.trim();
  if (backend) {
    try {
      const parsed = new URL(backend);
      if (parsed.protocol === 'https:') {
        return backend;
      }
    } catch {
      // Invalid URL: fall through to number-derived URL/null
    }
  }

  const trimmed = number.trim();
  if (!trimmed || isMaskedValue(trimmed)) {
    return null;
  }

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) {
    return null;
  }

  if (!trimmed.startsWith('+')) {
    return null;
  }

  return `https://wa.me/${digits}`;
}
